import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Language } from '../constants/language';
import { SEOUtil, SEOConfig } from '../utils/seo-util';

/**
 * useSEO - Hook for managing page-specific SEO
 *
 * This hook provides:
 * 1. Automatic SEO updates based on the current page and language
 * 2. Custom SEO configuration for dynamic content
 * 3. Language-aware metadata updates
 *
 * Usage for static pages:
 * ```tsx
 * // In Home.tsx
 * useSEO('home');
 * ```
 *
 * Usage for dynamic pages with custom content:
 * ```tsx
 * // In MinistryDetail.tsx
 * useSEO({
 *   title: `${ministry.name} | ZOE`,
 *   description: ministry.description,
 *   keywords: ['ministry', ministry.name],
 *   path: `/ministries/${ministry.id}`
 * });
 * ```
 */

interface UseSEOOptions {
  /**
   * Whether to automatically update SEO on mount
   * @default true
   */
  autoUpdate?: boolean;
}

type SEOInput = string | SEOConfig;

interface UseSEOResult {
  /**
   * Current language
   */
  language: Language;
  /**
   * Manually update SEO with a page key
   */
  updatePageSEO: (pageKey: string) => void;
  /**
   * Manually update SEO with custom config
   */
  updateCustomSEO: (config: SEOConfig) => void;
  /**
   * Set document language
   */
  setLanguage: (language: Language) => void;
}

export function useSEO(
  pageKeyOrConfig?: SEOInput,
  options: UseSEOOptions = {}
): UseSEOResult {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { autoUpdate = true } = options;

  const language = i18n.language as Language;

  /**
   * Update SEO for a predefined page
   */
  const updatePageSEO = useCallback((pageKey: string) => {
    SEOUtil.applyPageSEO(pageKey, language);
  }, [language]);

  /**
   * Update SEO with custom configuration
   */
  const updateCustomSEO = useCallback((config: SEOConfig) => {
    SEOUtil.updatePageSEO(config);
    SEOUtil.setLanguage(language);
  }, [language]);

  /**
   * Set document language
   */
  const setLanguage = useCallback((lang: Language) => {
    SEOUtil.setLanguage(lang);
  }, []);

  // Update SEO on mount and when language/location changes
  useEffect(() => {
    if (!autoUpdate) return;

    // Always update language first
    SEOUtil.setLanguage(language);

    if (!pageKeyOrConfig) {
      // Try to determine page from current path
      const pathToPageKey: Record<string, string> = {
        '/': 'home',
        '/ministries': 'ministries',
        '/events': 'events',
        '/schools': 'schools',
        '/store': 'store',
        '/about': 'about'
      };

      const pageKey = pathToPageKey[location.pathname];
      if (pageKey) {
        SEOUtil.applyPageSEO(pageKey, language);
      }
      return;
    }

    if (typeof pageKeyOrConfig === 'string') {
      // It's a page key
      SEOUtil.applyPageSEO(pageKeyOrConfig, language);
    } else {
      // It's a custom config
      SEOUtil.updatePageSEO(pageKeyOrConfig);
    }
  }, [pageKeyOrConfig, language, location.pathname, autoUpdate]);

  return {
    language,
    updatePageSEO,
    updateCustomSEO,
    setLanguage
  };
}

/**
 * usePageSEO - Simplified hook for static pages
 *
 * Usage:
 * ```tsx
 * usePageSEO('home'); // Uses predefined SEO for home page
 * ```
 */
export function usePageSEO(pageKey: string): void {
  useSEO(pageKey);
}

/**
 * useCustomSEO - Hook for pages with dynamic content
 *
 * Usage:
 * ```tsx
 * useCustomSEO(
 *   `${ministry.name} | ZOE`,
 *   ministry.description,
 *   {
 *     keywords: ['ministry', ministry.name],
 *     path: `/ministries/${ministry.id}`,
 *     image: ministry.image
 *   }
 * );
 * ```
 */
export function useCustomSEO(
  title: string,
  description: string,
  options?: {
    keywords?: string[];
    image?: string;
    path?: string;
    type?: 'website' | 'article';
  }
): void {
  const { i18n } = useTranslation();
  const language = i18n.language as Language;

  useEffect(() => {
    SEOUtil.setCustomSEO(title, description, language, options);
  }, [title, description, language, options]);
}

export default useSEO;
